// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ImprovePageLink from "@steiner/App/Components/ImprovePageLink/ImprovePageLink";
import * as React from "react";
import { connect } from "react-redux";

// XNG-Changes: added fields
const enum fields {
    showImproveLink = "showImproveLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImproveLink]: boolean;
    };
}
// XNG-Changes
const mapStateToProps = (state: ApplicationState) => {
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;
    const pagination = getProductListDataViewProperty(state, "pagination");

    return {
        currentPage: getCurrentPage(state),
        // XNG-Changes
        query: correctedQuery || productFilters.query,
        totalItemCount: pagination?.totalItemCount,
    };
};

type Props = OwnProps & HasCategoryContext & ReturnType<typeof mapStateToProps>;

export interface CategoryNameStyles {
    categoryNameWrapper?: InjectableCss;
    categoryName?: TypographyPresentationProps;
}

export const categoryNameStyles: CategoryNameStyles = {};

const styles = categoryNameStyles;

const CategoryName = ({
    category,
    currentPage,
    query,
    fields: { showImproveLink },
    totalItemCount,
}: Props) => {
    if (!category && !query) {
        return null;
    }

    const isSearchCrawler = getIsWebCrawler();
    return (
        <StyledWrapper {...styles.categoryNameWrapper}>
            {category && !query && (
                <Typography
                    {...styles.categoryName}
                    // XNG-Changes: changed variant to h1 always
                    as="h1"
                    variant="h1"
                    data-test-selector={`categoryName_${category.shortDescription ?? category.name}`}
                >
                    {category.shortDescription ?? category.name}
                </Typography>
            )}
            {query &&
                createWidgetElement("ProductList/ProductListProductCount", {
                    fields: { showProductsCountOnly: false },
                })}
            {showImproveLink && (totalItemCount ?? 0) > 0 && currentPage.type !== "CategoryDetailsPage" && <ImprovePageLink />}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(CategoryName)),
    definition: {
        group: "Category Details",
        icon: "folder-bookmark",
        allowedContexts: ["CategoryDetailsPage", "ProductDetailsPage", "ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.showImproveLink,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
